import {
    isContentVisible,
    type VisibilityAwareItem
} from "@/src/utils/content/isContentVisible"
import React, { type ComponentType } from "react"

export interface Content {
    _type: string
    [key: string]: any
}

export const filterContentByMarket = (content: unknown, locale: string | null): any => {
    if (!Array.isArray(content) || !locale) {
        return null
    }
    return content.find(item => isContentVisible(item, locale)) || null
}

export type Params = {
    locale: string
    category?: string
    slug?: string | string[]
    documentId?: string
    documentType?: string
}

export type ContentMappableComponent = {
    data: Content
    className?: string
    params?: Params
    isAboveTheFold?: boolean
    searchParams?: { [key: string]: string | string[] | undefined }
    carouselIndex?: number
}

export type ComponentMap = {
    [key: string]: ComponentType<any>
}

export type RenderedItem = {
    key: string
    className: string
    element: React.ReactNode
}

export type RenderContentItemsOpts = {
    componentMap: ComponentMap
    content: Content[]
    params: Params
    searchParams?: { [key: string]: string | string[] | undefined }
    isFirstBlockAboveTheFold?: boolean
}

export function renderContentItems({
    componentMap,
    content,
    params,
    searchParams,
    isFirstBlockAboveTheFold
}: RenderContentItemsOpts): RenderedItem[] {
    if (!content.length) return []

    let firstVisibleIndex = -1

    const visibleContent = content.reduce<
        Array<{ item: Content; originalIndex: number }>
    >((accumulator, item, originalIndex) => {
        if (
            !isContentVisible(item as VisibilityAwareItem, params.locale) ||
            item._type === "productSpotlightBlock"
        ) {
            return accumulator
        }

        if (firstVisibleIndex === -1) firstVisibleIndex = originalIndex

        return [...accumulator, { item, originalIndex }]
    }, [])

    const contentToRender = visibleContent

    return contentToRender
        .map(({ item, originalIndex }) => {
            const Component = componentMap[item._type]
            if (!Component) {
                return {
                    key: item._key || String(originalIndex),
                    className: item._type,
                    element: (
                        <div key={item._key || originalIndex}>
                            Error: Component of type {item._type} is not found!
                        </div>
                    )
                }
            }

            const isAboveTheFold =
                originalIndex === firstVisibleIndex && isFirstBlockAboveTheFold

            const needsCarouselIndex =
                item._type === "carouselBlock" || item._type === "depictCarouselBlock"

            return {
                key: item._key || String(originalIndex),
                className: item._type,
                element: (
                    <Component
                        key={item._key}
                        data={item}
                        params={params}
                        searchParams={searchParams}
                        className={item._type}
                        isAboveTheFold={isAboveTheFold}
                        {...(needsCarouselIndex && { carouselIndex: originalIndex })}
                    />
                )
            }
        })
        .filter(Boolean) as RenderedItem[]
}

export type Props = {
    componentMap: ComponentMap
    content: Content[]
    params: Params
    isFirstBlockAboveTheFold?: boolean
    searchParams?: { [key: string]: string | string[] | undefined }
}

const ContentMapper: React.FC<Props> = ({
    componentMap,
    content,
    params,
    searchParams,
    isFirstBlockAboveTheFold
}) => {
    const rendered = renderContentItems({
        componentMap,
        content,
        params,
        searchParams,
        isFirstBlockAboveTheFold
    })

    if (!rendered.length) return null

    return rendered.map(({ key, element }) => (
        <React.Fragment key={key}>{element}</React.Fragment>
    ))
}

export default ContentMapper
